import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import {
  Button,
  TextInput,
  Textarea,
  Select,
  Toggle,
  DatePicker,
  TimeField,
  File01,
  CalendarDate,
  CurrencyDollar,
  Monitor01,
  CheckCircle,
  InfoCircle,
  Plus,
  Trash01,
  TileGroup,
} from "@opendate-app/setlist";
import WizardShell, { ReviewRow, ReviewSection, WizardCard } from "../ClassesShared/WizardShell";
import PriceInput, { formatPriceInput } from "../ClassesShared/PriceInput";
import TintedAlert from "../ClassesShared/TintedAlert";
import ValidatedSelect from "../ClassesShared/ValidatedSelect";
import useWizardSteps, { newSessionKey, toggleChecked } from "../ClassesShared/useWizardSteps";
import useFocusAfterRemove from "../ClassesShared/useFocusAfterRemove";
import ClassImageField from "../ClassesShared/ClassImageField";
import useImageUpload from "../ClassesShared/useImageUpload";
import toFormData from "../ClassesShared/toFormData";
import { VISIBILITY_LABELS } from "../ClassesShared/classLabels";
import { formatPrice } from "../../shared/teamCurrencyFormatting";
import generateSlots, {
  autoEndDate, defaultTimeRange, fitSessionsToSlots, resizeAutoEndDate, scheduleForTemplate, shiftDate,
} from "./sessionScheduleGenerator";
import buildClassInstancePayload from "./buildPayload";
import { toDate, toValue, parseDateInput, needsPastStartWarning, closedRegistrationReason } from "./dateValue";
import SessionList from "./SessionList";
import ClearAttendanceModal from "./ClearAttendanceModal";
import ClosedRegistrationPublishModal from "../ClassesShared/ClosedRegistrationPublishModal";
import { initialGroupDiscount, groupDiscountErrors, groupDiscountSummary } from "./groupDiscount";
import pricingErrors from "./pricingValidation";
import templateWaitlist from "./waitlistDefaults";
import canVisitStep from "./stepNavigation";
import scheduleProblem, { NAME_MAX_LENGTH, scheduleProblemDetails, unsetSessionCount, unsetSessionsMessage } from "./scheduleValidation";
import { formatClock, formatClockRange, formatDayLabel, formatLongDate } from "./scheduleLabels";
import "./ClassScheduleWizard.css";

const STEPS = [
  { key: "details", label: "Class Details", icon: File01, subtitle: "Pick a template and adjust the basics. Defaults cascade from the template — you can override anything here." },
  { key: "schedule", label: "Schedule", icon: CalendarDate, subtitle: "Pick a date range and times. We'll generate the full list of sessions — you can fine-tune individual dates after." },
  { key: "pricing", label: "Pricing & Capacity", icon: CurrencyDollar, subtitle: "Set how many spots are available and what each one costs." },
  { key: "visibility", label: "Visibility", icon: Monitor01, subtitle: "Control who can find this class and how it appears." },
  { key: "review", label: "Review", icon: CheckCircle, subtitle: "Double-check everything before you save. Click any section to jump back and edit." },
];

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WEEKDAY_FULL = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const templateLabel = (template) => (template.archived ? `${template.name} (archived)` : template.name);

function isoToParts(iso, timeZone) {
  if (!iso) return { date: "", time: "" };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { date: "", time: "" };
  try {
    const date = new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
    const time = new Intl.DateTimeFormat("en-GB", { timeZone, hour: "2-digit", minute: "2-digit", hour12: false }).format(d);
    return { date, time };
  } catch (e) {
    const date = new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
    const time = new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false }).format(d);
    return { date, time };
  }
}

function sessionsFromInstance(classInstance, timeZone) {
  return (classInstance.class_sessions || []).map((s) => {
    const start = isoToParts(s.starts_at, timeZone);
    const end = isoToParts(s.ends_at, timeZone);
    return {
      key: `existing-${s.id}`,
      id: s.id,
      name: s.name || "",
      description: s.description || "",
      date: start.date,
      start: start.time,
      end: end.time,
      saved_date: start.date,
      saved_start: start.time,
      room_id: s.room_id ? String(s.room_id) : null,
      instructor_ids: (s.instructors || []).map((i) => i.id),
      loaded_instructor_ids: (s.instructors || []).map((i) => i.id),
      touched: true,
    };
  });
}

function ClassScheduleWizardApp(props) {
  const {
    classInstance, createPath, updatePath, indexPath, csrfToken,
    templates, venues, instructors, formatOptions, visibilityOptions, waitlistWindowOptions, waitlistWindowLabels, currencyCode, initialTemplateId, initialStep,
  } = props;
  const isEditing = !!classInstance;
  const canPublish = !isEditing || classInstance.status === "draft";
  const startStep = Math.max(0, STEPS.findIndex((s) => s.key === initialStep));

  const initialVenueTz = useMemo(() => {
    const vo = classInstance && venues.find((v) => v.id === classInstance.venue_ownership_id);
    return vo ? vo.timeZone : null;
  }, [classInstance, venues]);

  const [publishing, setPublishing] = useState(false);
  const [scheduleNotice, setScheduleNotice] = useState(null);
  const focusAfterRemove = useFocusAfterRemove();
  const [attendanceDecision, setAttendanceDecision] = useState(null);
  const [closedPublishReason, setClosedPublishReason] = useState(null);

  const [form, setForm] = useState(() => {
    const closes = isoToParts(classInstance?.registration_closes_at, initialVenueTz);
    const opens = isoToParts(classInstance?.registration_opens_at, initialVenueTz);
    return {
    class_template_id: classInstance?.class_template_id ? String(classInstance.class_template_id) : "",
    name: classInstance?.name || "",
    public_description: classInstance?.public_description || "",
    format: classInstance?.format || formatOptions[0] || "in_person",
    level: classInstance?.level || "",
    category: classInstance?.category || "",
    venue_ownership_id: classInstance?.venue_ownership_id ? String(classInstance.venue_ownership_id) : "",
    capacity: classInstance?.capacity ?? "",
    price: formatPriceInput(classInstance?.price ?? ""),
    waitlist_enabled: classInstance?.waitlist_enabled ?? true,
    waitlist_claim_window_hours: classInstance?.waitlist_claim_window_hours || waitlistWindowOptions[0],
    refund_window_days: classInstance?.refund_window_days ?? 7,
    ...initialGroupDiscount(classInstance),
    visibility: classInstance?.visibility || visibilityOptions[0] || "public_listing",
    registration_closes_date: closes.date,
    registration_closes_time: closes.time,
    registration_opens_date: opens.date,
    registration_opens_time: opens.time,
    };
  });

  const [schedule, setSchedule] = useState({ startDate: "", endDate: "", endDateAuto: false, weekdays: [], timesByWeekday: {} });
  const [sessions, setSessions] = useState(() => (isEditing ? sessionsFromInstance(classInstance, initialVenueTz) : []));

  const [waitlistEditedFields, setWaitlistEditedFields] = useState([]);

  const setField = (key, val) => setForm((prev) => ({ ...prev, [key]: val }));
  const setWaitlistField = (key, val) => {
    setWaitlistEditedFields((prev) => (prev.includes(key) ? prev : [...prev, key]));
    setField(key, val);
  };

  const discountErrors = groupDiscountErrors(form);
  const discountInvalid = Object.keys(discountErrors).length > 0;
  const spotsTaken = isEditing ? Number(classInstance.spots_taken || 0) : 0;
  const priceErrors = pricingErrors(form, { spotsTaken });
  const pricingInvalid = Object.keys(priceErrors).length > 0;
  const closesIncomplete = !form.registration_closes_date !== !form.registration_closes_time;
  const firstSessionStart = sessions.map((s) => (s.date && s.start ? `${s.date}T${s.start}` : null)).filter(Boolean).sort()[0];
  const closesAfterStart = !closesIncomplete && !!form.registration_closes_date && !!firstSessionStart
    && `${form.registration_closes_date}T${form.registration_closes_time}` > firstSessionStart;
  const opensIncomplete = !form.registration_opens_date !== !form.registration_opens_time;
  const opensValue = form.registration_opens_date && form.registration_opens_time ? `${form.registration_opens_date}T${form.registration_opens_time}` : null;
  const closesValue = form.registration_closes_date && form.registration_closes_time ? `${form.registration_closes_date}T${form.registration_closes_time}` : null;
  const opensAfterCloses = !!opensValue && !!closesValue && opensValue >= closesValue;
  const opensAfterStart = !!opensValue && !!firstSessionStart && opensValue >= firstSessionStart;
  const opensInvalid = opensIncomplete || opensAfterCloses || opensAfterStart;
  let opensError = null;
  if (opensIncomplete) opensError = "Pick both a date and a time, or clear them.";
  else if (opensAfterCloses) opensError = "Registration has to open before it closes.";
  else if (opensAfterStart) opensError = "Registration has to open before the first session starts.";

  useLayoutEffect(() => {
    setScheduleNotice(null);
  }, [schedule]);

  const selectedVenue = venues.find((v) => String(v.id) === String(form.venue_ownership_id));
  const selectedTemplate = templates.find((t) => String(t.id) === String(form.class_template_id));
  const templateImageUrl = isEditing ? null : selectedTemplate?.main_image_url;
  const image = useImageUpload(isEditing ? classInstance.main_image_url : templateImageUrl);
  const imageFromTemplate = !!templateImageUrl && !image.changed;
  const rooms = selectedVenue ? selectedVenue.rooms : [];
  const defaultClassTime = () => ({ ...defaultTimeRange(selectedTemplate?.default_session_duration_minutes), auto: true });

  const applyTemplate = (templateId) => {
    setField("class_template_id", templateId);
    const template = templates.find((t) => String(t.id) === String(templateId));
    if (!template) return;

    setForm((prev) => ({
      ...prev,
      name: prev.name || template.name,
      format: template.format || prev.format,
      category: prev.category || template.category || "",
      capacity: prev.capacity === "" ? (template.default_capacity ?? "") : prev.capacity,
      price: prev.price === "" ? formatPriceInput(template.default_price ?? "") : prev.price,
      ...templateWaitlist(template, { isEditing, editedFields: waitlistEditedFields }),
    }));

    if (!isEditing) {
      image.restore();
      const templateSessions = template.class_template_sessions || [];
      const nextSchedule = scheduleForTemplate(schedule, {
        duration: template.default_session_duration_minutes,
        sessionCount: templateSessions.length,
      });
      setSchedule(nextSchedule);
      setSessions(fitSessionsToSlots(
        templateSessions.map((ts) => ({
          key: newSessionKey(),
          name: ts.name || "",
          description: ts.description || "",
          date: "",
          start: "",
          end: "",
          room_id: null,
          instructor_ids: [],
        })),
        generateSlots(nextSchedule)
      ));
    }
  };

  useEffect(() => {
    if (!isEditing && initialTemplateId) applyTemplate(String(initialTemplateId));
  }, []);

  const onStartDateChange = (date) => {
    const value = toValue(date);
    setSchedule((prev) => {
      if (!value) return { ...prev, startDate: "" };
      const wday = new Date(`${value}T00:00:00`).getDay();
      const weekdays = prev.weekdays.length ? prev.weekdays : [wday];
      const timesByWeekday = { ...prev.timesByWeekday };
      weekdays.forEach((wd) => {
        if (!(timesByWeekday[wd] || []).length) timesByWeekday[wd] = [defaultClassTime()];
      });
      const next = { ...prev, startDate: value, weekdays, timesByWeekday };
      if (!prev.endDate) return { ...next, endDate: autoEndDate(next, sessions.length), endDateAuto: true };
      if (!prev.endDateAuto) return next;
      if (sessions.length > 0) return resizeAutoEndDate(next, sessions.length);
      return { ...next, endDate: prev.startDate ? shiftDate(prev.endDate, prev.startDate, value) : autoEndDate(next, 0) };
    });
  };

  const setWeekdays = (selected) => {
    setSchedule((prev) => {
      const weekdays = [...selected].sort((a, b) => a - b);
      const timesByWeekday = {};
      weekdays.forEach((wday) => {
        const ranges = prev.timesByWeekday[wday];
        timesByWeekday[wday] = ranges && ranges.length > 0 ? ranges : [defaultClassTime()];
      });
      return resizeAutoEndDate({ ...prev, weekdays, timesByWeekday }, sessions.length);
    });
  };

  const setClassTime = (wday, idx, field, value) => {
    setSchedule((prev) => {
      const ranges = (prev.timesByWeekday[wday] || []).map((r, i) => (i === idx ? { ...r, [field]: value, auto: false } : r));
      return { ...prev, timesByWeekday: { ...prev.timesByWeekday, [wday]: ranges } };
    });
  };
  const addClassTime = (wday) =>
    setSchedule((prev) => resizeAutoEndDate({
      ...prev,
      timesByWeekday: { ...prev.timesByWeekday, [wday]: [...(prev.timesByWeekday[wday] || []), defaultClassTime()] },
    }, sessions.length));
  const removeClassTime = (wday, idx) => {
    focusAfterRemove(`[data-class-time-remove="${wday}"]`, idx, `[data-class-time-add="${wday}"]`);
    setSchedule((prev) => resizeAutoEndDate({
      ...prev,
      timesByWeekday: { ...prev.timesByWeekday, [wday]: (prev.timesByWeekday[wday] || []).filter((_, i) => i !== idx) },
    }, sessions.length));
  };

  useEffect(() => {
    const slots = generateSlots(schedule);
    if (slots.length === 0) return;
    setSessions((prev) => fitSessionsToSlots(prev, slots));
  }, [schedule]);

  const generate = () => {
    const slots = generateSlots(schedule);
    if (slots.length === 0) {
      setScheduleNotice("Pick a start/end date, at least one weekday, and a class time first.");
      return;
    }
    if (sessions.some((s) => s.touched) && !window.confirm("Regenerating replaces the dates and times on sessions you've edited. Continue?")) {
      return;
    }
    setSessions((prev) => {
      const next = prev.map((s, i) =>
        slots[i] ? { ...s, date: slots[i].date, start: slots[i].start, end: slots[i].end, touched: false } : { ...s, date: "", start: "", end: "", touched: false }
      );
      for (let i = prev.length; i < slots.length; i += 1) {
        next.push({ key: newSessionKey(), name: `Session ${i + 1}`, description: "", date: slots[i].date, start: slots[i].start, end: slots[i].end, room_id: null, instructor_ids: [] });
      }
      return next;
    });
  };

  const changeSession = (key, field, value) => setSessions((prev) => prev.map((s) => (s.key === key ? { ...s, [field]: value, touched: true } : s)));
  const addSession = () => {
    const key = newSessionKey();
    setSessions((prev) => [...prev, { key, name: `Session ${prev.length + 1}`, description: "", date: "", start: "", end: "", room_id: null, instructor_ids: [], touched: true }]);
    return key;
  };
  const removeSession = (key) => setSessions((prev) => prev.filter((s) => s.key !== key));
  const clearSessions = () => setSessions([]);

  const validate = () => {
    if (!form.class_template_id) return { step: 0, message: "Pick a template for this class.", field: "class-template" };
    if (form.name.trim() === "") return { step: 0, message: "Give the class a name.", field: "class-name" };
    if (form.name.trim().length > NAME_MAX_LENGTH) return { step: 0, message: `Class names can be up to ${NAME_MAX_LENGTH} characters.`, field: "class-name" };
    const scheduleDetails = scheduleProblemDetails(form, sessions);
    if (scheduleDetails) return { step: 1, ...scheduleDetails };
    if (priceErrors.refund_window_days) return { step: 2, message: "Fix the refund window before saving.", field: "refund_window_days" };
    if (pricingInvalid) return { step: 2, message: "Fix the highlighted pricing fields before saving.", field: Object.keys(priceErrors)[0] };
    if (discountInvalid) return { step: 2, message: "Fix the group discount before saving.", field: discountErrors.threshold ? "group_discount_threshold" : "group_discount_percentage" };
    if (opensInvalid) return { step: 3, message: opensError, field: form.registration_opens_date ? "registration-opens-time" : "registration-opens-date" };
    if (closesIncomplete) return { step: 3, message: "Pick both a date and a time for registration to close, or clear them.", field: form.registration_closes_date ? "registration-closes-time" : "registration-closes-date" };
    return null;
  };

  const submit = async ({ attendanceChoice = null, publish = false, closedConfirmed = false } = {}) => {
    if (saving && !attendanceChoice) return;
    const problem = validate();
    if (problem) {
      wizard.showProblem(problem);
      return;
    }

    if (publish && !attendanceChoice && !closedConfirmed) {
      const reason = closedRegistrationReason({
        closesDate: form.registration_closes_date,
        closesTime: form.registration_closes_time,
        sessions,
        timeZone: selectedVenue?.timeZone,
      });
      if (reason) {
        setClosedPublishReason(reason);
        return;
      }
    }

    wizard.startSaving();
    setPublishing(publish);
    const headers = { "X-CSRF-Token": csrfToken };
    try {
      const body = toFormData({
        class_instance: {
          ...buildClassInstancePayload({
            form,
            sessions,
            existingSessions: isEditing ? classInstance.class_sessions || [] : null,
            timeZone: selectedVenue?.timeZone,
            attendanceChoice,
          }),
          ...image.payload,
        },
        ...(publish ? { publish: true } : {}),
      });
      const resp = updatePath ? await axios.patch(updatePath, body, { headers }) : await axios.post(createPath, body, { headers });
      window.location.href = `${indexPath}/${resp.data.id}`;
    } catch (e) {
      const decision = e.response?.status === 409 ? e.response.data?.attendance_decision : null;
      if (decision) {
        setAttendanceDecision({ ...decision, publish });
        return;
      }
      wizard.failSave(e, "Could not save this class.");
    }
  };

  const isStepComplete = (index) => {
    switch (index) {
      case 0:
        return form.name.trim().length > 0 && form.name.trim().length <= NAME_MAX_LENGTH && !!form.class_template_id;
      case 1:
        return !scheduleProblem(form, sessions);
      case 2:
        return !pricingInvalid && !discountInvalid;
      case 3:
        return !!form.visibility && !closesIncomplete && !opensInvalid;
      default:
        return false;
    }
  };

  const wizard = useWizardSteps({
    steps: STEPS,
    initialStep: startStep,
    isEditing,
    isStepComplete,
    canVisit: (index, maxStepReached) => canVisitStep(index, { isEditing, maxStepReached, isStepComplete }),
    resetOn: [form, sessions, schedule],
  });
  const { step, saving, fieldErrors, goTo } = wizard;

  const unsetSessionsNotice = unsetSessionsMessage(unsetSessionCount(sessions));
  const nextBlocked = (wizard.stepKey === "schedule" && !!unsetSessionsNotice)
    || (wizard.stepKey === "pricing" && (pricingInvalid || discountInvalid))
    || (wizard.stepKey === "visibility" && (closesIncomplete || opensInvalid));

  const reviewSessionLine = (s, i) => {
    const roomName = rooms.find((r) => String(r.id) === String(s.room_id))?.name;
    const names = (s.instructor_ids || []).map((id) => instructors.find((ins) => ins.id === id)?.name).filter(Boolean);
    const bits = [s.date && formatDayLabel(s.date), s.start && formatClockRange(s.start, s.end), roomName, names.join(", ")].filter(Boolean);
    return (
      <li key={s.key}>
        <strong>{`Session ${i + 1}`}</strong>
        {` — ${s.name || "Untitled"} · ${bits.join(" · ") || "unscheduled"}`}
      </li>
    );
  };

  const trustSavedSessions = isEditing && classInstance.status !== "draft" && selectedVenue?.timeZone === initialVenueTz;
  const pastSessionCount = sessions.filter((s) => needsPastStartWarning(s, selectedVenue?.timeZone, new Date(), trustSavedSessions)).length;
  const pastStartWarning = pastSessionCount > 0 && (
    <TintedAlert
      color="warning"
      icon={InfoCircle}
      title={pastSessionCount === 1 ? "1 session starts in the past" : `${pastSessionCount} sessions start in the past`}
      description="You can still save, but registrants can't attend a session that has already started. Check the dates before you publish."
    />
  );

  const capacityHint = spotsTaken > 0
    ? `Currently ${spotsTaken} ${spotsTaken === 1 ? "spot is" : "spots are"} taken. Reducing below ${spotsTaken} isn't allowed.`
    : (form.class_template_id ? "Pre-filled from the template default." : undefined);

  const scheduleSummary = schedule.startDate && schedule.endDate
    ? `${formatLongDate(schedule.startDate)} – ${formatLongDate(schedule.endDate)} · ${schedule.weekdays.length} day/wk`
    : null;

  const finishButton = canPublish ? (
    <div className="class-schedule-wizard__finish">
      <Button variant="secondary" onClick={() => submit()} aria-disabled={saving || undefined}>{saving && !publishing ? "Saving…" : "Save as Draft"}</Button>
      <Button variant="primary" onClick={() => submit({ publish: true })} aria-disabled={saving || undefined}>{saving && publishing ? "Publishing…" : "Save & Publish"}</Button>
    </div>
  ) : (
    <Button variant="primary" onClick={() => submit()} aria-disabled={saving || undefined}>{saving ? "Saving…" : "Save Class"}</Button>
  );

  return (
    <WizardShell
      className="class-schedule-wizard"
      title={isEditing ? "Edit Class" : "Schedule a Class"}
      subtitle="Pre-fill from a template, then adjust the dates, venue, instructors, and pricing for this class."
      cancelHref={indexPath}
      saveLabel="Save"
      onSave={() => submit()}
      errorTitle="Could not save this class"
      setupTitle="Class setup"
      wizard={wizard}
      nextDisabled={nextBlocked}
      finish={finishButton}
    >
      {step === 0 && (
        <>
          <WizardCard>
            <div className="classes-form-row">
              <ValidatedSelect
                id="class-template"
                label="Template"
                error={fieldErrors["class-template"]}
                required
                hint="Pre-fills format, default capacity, price, and waitlist."
                placeholder="Select a template…"
                options={templates.map((t) => ({ value: String(t.id), label: templateLabel(t) }))}
                value={form.class_template_id}
                onChange={(v) => applyTemplate(v)}
              />
              <TextInput
                data-field="class-name"
                label="Class name"
                error={fieldErrors["class-name"]}
                hint="Public-facing name for this class. Defaults to the template name."
                placeholder="Public-facing name"
                maxLength={NAME_MAX_LENGTH}
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
              />
            </div>
          </WizardCard>

          <WizardCard title="Description" supportingText="Optional override for this class. Leave blank to use the template description.">
            <Textarea
              label="Public description"
              rows={4}
              placeholder={selectedTemplate?.description || "What registrants will get out of this class. Shows on the public class page."}
              value={form.public_description}
              onChange={(e) => setField("public_description", e.target.value)}
            />
          </WizardCard>

          <WizardCard title="Image" supportingText="Shown on the class page, the catalog card and checkout. Without one, the class page shows a color gradient.">
            <ClassImageField
              label="Class image"
              imageUrl={image.imageUrl}
              fileName={image.fileName}
              sourceLabel={imageFromTemplate ? "From template" : null}
              onImage={image.upload}
              onRemove={image.remove}
            />
          </WizardCard>
        </>
      )}

      {step === 1 && (
        <>
          <WizardCard
            title="Venue"
            supportingText={selectedVenue?.timeZone ? `Session times are in the venue's time zone (${selectedVenue.timeZone}).` : "Session times use the venue's time zone."}
          >
            <div className="classes-wizard__half">
              <ValidatedSelect
                id="class-venue"
                label="Venue"
                error={fieldErrors["class-venue"]}
                required
                placeholder="Select a venue…"
                options={venues.map((v) => ({ value: String(v.id), label: v.name }))}
                value={form.venue_ownership_id}
                onChange={(v) => setField("venue_ownership_id", v)}
              />
            </div>
          </WizardCard>

          <WizardCard title="Class Start & End Dates" summary={scheduleSummary}>
            <div className="classes-form-row">
              <DatePicker
                label="Start date"
                placeholder="Select date"
                parseDate={parseDateInput}
                value={toDate(schedule.startDate)}
                onChange={onStartDateChange}
              />
              <DatePicker
                label="End date"
                placeholder="Select date"
                parseDate={parseDateInput}
                value={toDate(schedule.endDate)}
                onChange={(date) => setSchedule((p) => ({ ...p, endDate: toValue(date), endDateAuto: false }))}
              />
            </div>
            <span className="class-schedule-wizard__chip-label" id="class-schedule-repeat-on">Repeat On</span>
            <TileGroup
              type="checkbox"
              columns={7}
              aria-labelledby="class-schedule-repeat-on"
              items={WEEKDAYS.map((label, wday) => ({ value: wday, label }))}
              values={schedule.weekdays}
              onChange={setWeekdays}
            />
          </WizardCard>

          {schedule.weekdays.length > 0 && (
            <WizardCard title="Class Times">
              {schedule.weekdays.map((wday) => {
                const ranges = schedule.timesByWeekday[wday] || [];
                return (
                  <div key={wday} className="class-schedule-wizard__weekday">
                    <div className="class-schedule-wizard__weekday-head">
                      <span>{WEEKDAY_FULL[wday]}</span>
                      <span className="class-schedule-wizard__weekday-count">{`${ranges.length} class time${ranges.length === 1 ? "" : "s"}`}</span>
                    </div>
                    {ranges.map((range, idx) => (
                      <div key={idx} className="class-schedule-wizard__time-row">
                        <div>
                          <label className="sr-only" htmlFor={`class-time-${wday}-${idx}-start`}>{`${WEEKDAY_FULL[wday]} start ${idx + 1}`}</label>
                          <TimeField id={`class-time-${wday}-${idx}-start`} value={range.start || ""} onChange={(v) => setClassTime(wday, idx, "start", v)} />
                        </div>
                        <div>
                          <label className="sr-only" htmlFor={`class-time-${wday}-${idx}-end`}>{`${WEEKDAY_FULL[wday]} end ${idx + 1}`}</label>
                          <TimeField id={`class-time-${wday}-${idx}-end`} value={range.end || ""} onChange={(v) => setClassTime(wday, idx, "end", v)} />
                        </div>
                        <Button variant="tertiary" size="sm" icon={Trash01} aria-label={`Remove ${WEEKDAY_FULL[wday]} time ${idx + 1}`} data-class-time-remove={wday} onClick={() => removeClassTime(wday, idx)} />
                      </div>
                    ))}
                    <Button variant="link-color" size="sm" icon={Plus} data-class-time-add={wday} onClick={() => addClassTime(wday)}>Add Class Time</Button>
                  </div>
                );
              })}
            </WizardCard>
          )}

          {schedule.weekdays.length > 0 && (
            <div>
              <Button variant="primary" icon={CalendarDate} onClick={generate} aria-describedby="generate-schedule-notice">Generate Schedule</Button>
              <p id="generate-schedule-notice" className="classes-field-error" role="alert">{scheduleNotice}</p>
            </div>
          )}

          {pastStartWarning}

          <SessionList
            sessions={sessions}
            rooms={rooms}
            instructors={instructors}
            timeZone={selectedVenue?.timeZone}
            trustSavedSessions={trustSavedSessions}
            confirmClear={isEditing}
            hint={sessions.length === 0 ? "Pick a template on the previous step to inherit its sessions — set a start date above and we'll fill in the dates and times for you. Add one manually with Add Date." : undefined}
            onChange={changeSession}
            onAdd={addSession}
            onRemove={removeSession}
            onClear={clearSessions}
          />

          {unsetSessionsNotice && <p className="classes-text-secondary small mb-0" data-field="unset-sessions-notice">{unsetSessionsNotice}</p>}
        </>
      )}

      {step === 2 && (
        <>
          <WizardCard title="Capacity">
            <div className="classes-wizard__half">
              <TextInput
                data-field="capacity"
                label="Maximum spots"
                type="number"
                min="1"
                hint={capacityHint}
                value={form.capacity}
                error={priceErrors.capacity}
                onChange={(e) => setField("capacity", e.target.value)}
              />
            </div>
            <div className="classes-wizard__divided">
              <Toggle
                label="Enable waitlist"
                description={`When full, new registrants join a waitlist. Spots are offered on a first-come basis, each held for ${waitlistWindowLabels[form.waitlist_claim_window_hours]}.`}
                checked={form.waitlist_enabled}
                onChange={(e) => setWaitlistField("waitlist_enabled", toggleChecked(e))}
              />
            </div>
            {form.waitlist_enabled && (
              <div className="classes-wizard__half mt-3">
                <Select
                  label="Claim window"
                  options={waitlistWindowOptions.map((h) => ({ value: h, label: `${h} hours` }))}
                  value={form.waitlist_claim_window_hours}
                  onChange={(v) => setWaitlistField("waitlist_claim_window_hours", Number(v))}
                />
              </div>
            )}
          </WizardCard>

          <WizardCard title="Pricing">
            <div className="classes-wizard__half">
              <PriceInput
                data-field="price"
                label="Price per spot"
                currencyCode={currencyCode}
                hint={form.class_template_id ? "Pre-filled from the template default." : undefined}
                value={form.price}
                error={priceErrors.price}
                onChange={(value) => setField("price", value)}
              />
            </div>
            <div className="classes-wizard__half mt-3">
              <TextInput
                data-field="refund_window_days"
                label="Refund window"
                type="number"
                min="0"
                step="1"
                trailingText="days before"
                hint="Attendees can cancel online for a full refund until this many days before the first session."
                value={form.refund_window_days}
                error={priceErrors.refund_window_days}
                onChange={(e) => setField("refund_window_days", e.target.value)}
              />
            </div>
            <div className="classes-wizard__divided">
              <Toggle
                label="Group discount"
                description="Take a percentage off every spot when one order registers a group."
                checked={form.group_discount_enabled}
                onChange={(e) => setField("group_discount_enabled", toggleChecked(e))}
              />
            </div>
            {form.group_discount_enabled && (
              <div className="class-schedule-wizard__discount d-flex flex-wrap align-items-start mt-3">
                <div className="class-schedule-wizard__discount-field">
                  <TextInput data-field="group_discount_threshold" label="Group size" type="number" min="2" step="1" trailingText="or more" value={form.group_discount_threshold} error={discountErrors.threshold} onChange={(e) => setField("group_discount_threshold", e.target.value)} />
                </div>
                <div className="class-schedule-wizard__discount-field">
                  <TextInput data-field="group_discount_percentage" label="Discount" type="number" min="1" max="100" step="0.01" trailingText="%" value={form.group_discount_percentage} error={discountErrors.percentage} onChange={(e) => setField("group_discount_percentage", e.target.value)} />
                </div>
              </div>
            )}
          </WizardCard>
        </>
      )}

      {step === 3 && (
        <>
          <WizardCard title="Listing">
            <div className="classes-wizard__half">
              <Select
                label="Visibility"
                hint="Controls where this class shows up to the public."
                options={visibilityOptions.map((value) => ({ value, label: VISIBILITY_LABELS[value] || value }))}
                value={form.visibility}
                onChange={(v) => setField("visibility", v)}
              />
            </div>
          </WizardCard>
          <WizardCard
            title="Registration opens"
            supportingText={`Leave blank to open registration as soon as the class is published. Before then the class is listed, but nobody can register.${selectedVenue?.timeZone ? ` Times are in ${selectedVenue.timeZone}.` : ""}`}
          >
            <div className="classes-form-row align-items-end">
              <DatePicker
                data-field="registration-opens-date"
                label="Open on"
                placeholder="Select date"
                value={toDate(form.registration_opens_date)}
                onChange={(date) => setField("registration_opens_date", toValue(date))}
                parseDate={parseDateInput}
              />
              <TimeField id="registration-opens-time" data-field="registration-opens-time" label="At" value={form.registration_opens_time || ""} onChange={(v) => setField("registration_opens_time", v)} />
            </div>
            {opensError && <div className="text-danger small mt-2" role="alert">{opensError}</div>}
            {(form.registration_opens_date || form.registration_opens_time) && (
              <Button variant="link-color" size="sm" className="mt-2" onClick={() => setForm((prev) => ({ ...prev, registration_opens_date: "", registration_opens_time: "" }))}>
                Clear, open when published
              </Button>
            )}
          </WizardCard>
          <WizardCard
            title="Registration closes"
            supportingText={`Leave blank to keep registration open until the first session starts.${selectedVenue?.timeZone ? ` Times are in ${selectedVenue.timeZone}.` : ""}`}
          >
            <div className="classes-form-row align-items-end">
              <DatePicker
                data-field="registration-closes-date"
                label="Close on"
                placeholder="Select date"
                value={toDate(form.registration_closes_date)}
                onChange={(date) => setField("registration_closes_date", toValue(date))}
                parseDate={parseDateInput}
              />
              <TimeField id="registration-closes-time" data-field="registration-closes-time" label="At" value={form.registration_closes_time || ""} onChange={(v) => setField("registration_closes_time", v)} />
            </div>
            {closesIncomplete && <div className="text-danger small mt-2" role="alert">Pick both a date and a time, or clear them.</div>}
            {closesAfterStart && <div className="text-muted small mt-2">That's after the first session starts, so registration will close when the first session starts.</div>}
            {(form.registration_closes_date || form.registration_closes_time) && (
              <Button variant="link-color" size="sm" className="mt-2" onClick={() => setForm((prev) => ({ ...prev, registration_closes_date: "", registration_closes_time: "" }))}>
                Clear, close when the first session starts
              </Button>
            )}
          </WizardCard>
          <TintedAlert
            color="brand"
            icon={InfoCircle}
            title="Publishing creates this on your calendar"
            description={canPublish
              ? "Each session shows up as its own block on the venue calendar, badged as a Class. Publish with Save & Publish on the Review step, or later from the class page."
              : "Each session shows up as its own block on the venue calendar, badged as a Class."}
          />
        </>
      )}

      {step === 4 && (
        <>
          <ReviewSection title="Class Details" onEdit={() => goTo("details")}>
            <ReviewRow label="Template" value={selectedTemplate ? templateLabel(selectedTemplate) : "—"} />
            <ReviewRow label="Class name" value={form.name || "—"} />
            <ReviewRow
              label="Description"
              value={form.public_description.trim() || (selectedTemplate?.description ? (
                <>
                  {selectedTemplate.description}
                  <div className="classes-wizard__review-list-detail">From template</div>
                </>
              ) : "—")}
            />
            <ReviewRow
              label="Image"
              value={image.imageUrl ? (
                <>
                  <img src={image.imageUrl} alt="Class image" className="classes-image-preview" />
                  {imageFromTemplate && <div className="classes-wizard__review-list-detail">From template</div>}
                </>
              ) : "No image"}
            />
          </ReviewSection>
          <ReviewSection title="Schedule" onEdit={() => goTo("schedule")}>
            <ReviewRow label="Venue" value={selectedVenue?.name || "—"} />
            <ReviewRow
              label="Sessions"
              value={sessions.length === 0 ? "No sessions" : <ol className="classes-wizard__review-list classes-wizard__review-list--plain">{sessions.map(reviewSessionLine)}</ol>}
            />
          </ReviewSection>
          <ReviewSection title="Pricing & Capacity" onEdit={() => goTo("pricing")}>
            <ReviewRow label="Maximum spots" value={form.capacity || "—"} />
            <ReviewRow label="Waitlist" value={form.waitlist_enabled ? `Enabled · held ${waitlistWindowLabels[form.waitlist_claim_window_hours]}` : "Disabled"} />
            <ReviewRow label="Price per spot" value={form.price === "" ? "—" : (priceErrors.price ? form.price : formatPrice(form.price, currencyCode))} />
            <ReviewRow label="Group discount" value={groupDiscountSummary(form)} />
            <ReviewRow label="Refund window" value={`${form.refund_window_days} days before the first session`} />
          </ReviewSection>
          <ReviewSection title="Visibility" onEdit={() => goTo("visibility")}>
            <ReviewRow label="Listing" value={VISIBILITY_LABELS[form.visibility] || form.visibility} />
            <ReviewRow
              label="Registration opens"
              value={opensValue ? `${formatDayLabel(form.registration_opens_date)} · ${formatClock(form.registration_opens_time)}` : "When the class is published"}
            />
            <ReviewRow
              label="Registration closes"
              value={form.registration_closes_date && form.registration_closes_time && !closesAfterStart
                ? `${formatDayLabel(form.registration_closes_date)} · ${formatClock(form.registration_closes_time)}`
                : "When the first session starts"}
            />
          </ReviewSection>
          {pastStartWarning}
          <TintedAlert
            color="brand"
            icon={InfoCircle}
            title={isEditing ? "Ready to save your changes?" : "Ready to save?"}
            description={canPublish
              ? "Save as Draft keeps this class hidden until you publish it from the class page. Save & Publish opens registration, lists it in the catalog and puts its sessions on the venue calendar."
              : "Saved changes apply to this class right away, including its public page if it's published."}
          />
        </>
      )}

      {closedPublishReason && (
        <ClosedRegistrationPublishModal
          reason={closedPublishReason}
          onClose={() => setClosedPublishReason(null)}
          onConfirm={() => {
            setClosedPublishReason(null);
            submit({ publish: true, closedConfirmed: true });
          }}
        />
      )}

      {attendanceDecision && (
        <ClearAttendanceModal
          marksCount={attendanceDecision.marks_count}
          sessionCount={attendanceDecision.session_ids.length}
          onCancel={() => { setAttendanceDecision(null); wizard.setSaving(false); }}
          onChoose={(clear) => {
            const { session_ids: sessionIds, publish } = attendanceDecision;
            setAttendanceDecision(null);
            submit({ attendanceChoice: { sessionIds, clear }, publish });
          }}
        />
      )}
    </WizardShell>
  );
}

ClassScheduleWizardApp.propTypes = {
  classInstance: PropTypes.object,
  createPath: PropTypes.string,
  updatePath: PropTypes.string,
  indexPath: PropTypes.string.isRequired,
  csrfToken: PropTypes.string.isRequired,
  templates: PropTypes.array.isRequired,
  venues: PropTypes.array.isRequired,
  instructors: PropTypes.array.isRequired,
  formatOptions: PropTypes.arrayOf(PropTypes.string),
  visibilityOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
  waitlistWindowOptions: PropTypes.arrayOf(PropTypes.number).isRequired,
  waitlistWindowLabels: PropTypes.objectOf(PropTypes.string).isRequired,
  currencyCode: PropTypes.string,
  initialTemplateId: PropTypes.number,
  initialStep: PropTypes.string,
};

ClassScheduleWizardApp.defaultProps = {
  classInstance: null,
  formatOptions: ["in_person", "online", "blended"],
  currencyCode: "USD",
  initialTemplateId: null,
  initialStep: null,
};

export default ClassScheduleWizardApp;
